import { getPushPublicKey, subscribePush, unsubscribePush } from "./api";

export type PushStatus = "unsupported" | "needs-install" | "default" | "enabled" | "denied" | "unconfigured";

function isIos(): boolean {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
}

function isStandalone(): boolean {
  return window.matchMedia("(display-mode: standalone)").matches || ("standalone" in navigator && navigator.standalone === true);
}

function urlBase64ToArrayBuffer(value: string): ArrayBuffer {
  const padding = "=".repeat((4 - (value.length % 4)) % 4);
  const base64 = (value + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  return Uint8Array.from([...raw].map((char) => char.charCodeAt(0))).buffer;
}

export function getPushCapabilityStatus(): PushStatus {
  if (isIos() && !isStandalone()) return "needs-install";
  if (!("serviceWorker" in navigator) || !("PushManager" in window) || !("Notification" in window)) return "unsupported";
  if (Notification.permission === "granted") return "enabled";
  if (Notification.permission === "denied") return "denied";
  return "default";
}

export async function refreshPushSubscription(): Promise<PushStatus> {
  const status = getPushCapabilityStatus();
  if (status !== "enabled") return status;

  const registration = await navigator.serviceWorker.ready;
  const existing = await registration.pushManager.getSubscription();
  if (!existing) return "default";

  await subscribePush(existing.toJSON());
  return "enabled";
}

export async function enablePushNotifications(): Promise<PushStatus> {
  const status = getPushCapabilityStatus();
  if (status === "needs-install" || status === "unsupported" || status === "denied") return status;

  const permission = await Notification.requestPermission();
  if (permission === "denied") return "denied";
  if (permission !== "granted") return "default";

  let publicKey: string;
  try {
    publicKey = (await getPushPublicKey()).publicKey;
  } catch {
    return "unconfigured";
  }

  const registration = await navigator.serviceWorker.ready;
  const existing = await registration.pushManager.getSubscription();
  const subscription =
    existing ??
    (await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToArrayBuffer(publicKey),
    }));

  await subscribePush(subscription.toJSON());
  return "enabled";
}

export async function disablePushNotifications(): Promise<PushStatus> {
  const registration = await navigator.serviceWorker.ready;
  const existing = await registration.pushManager.getSubscription();
  if (existing) {
    await unsubscribePush(existing.endpoint);
    await existing.unsubscribe();
  }
  return getPushCapabilityStatus() === "enabled" ? "default" : getPushCapabilityStatus();
}
